import React, {useEffect, useReducer, useRef, useState} from "react";
import '../utils/i18n';
import Map, {MapAPI} from "./map";
import SearchSidebar from "./search_sidebar";
import {MapInfo, SearchQueryTagList} from "../models/search_query";
import "../../../assets/stylesheets/reshaped/gintoTheme/theme.css";
import {RatingProfileId, SearchAction, SearchState} from "../models/models";
import {RatingProfileSwitcher} from "./rating_profile_switcher";
import {useSearchState} from "../utils/routing_hooks";
import {geoLocationFromPlaceSearchQueryTag} from "../utils/helpers";
import {GeoPositionProvider} from "../utils/geo_position_provider";

function searchReducer(state: SearchState, action: SearchAction): SearchState {
    switch (action.type) {
        case 'MAP_INFO_CHANGED': {
            return {
                ...state,
                mapInfo: action.mapInfo
            }
        }
        case 'TAGS_CHANGED': {
            return {
                ...state,
                searchQueryTags: action.searchQueryTags
            }
        }
        case 'RATING_PROFILE_SELECTION_CHANGED': {
            return {
                ...state,
                ratingProfileId: action.ratingProfileId
            }
        }
    }
}

export function SearchMap() {
    const [initialSearchState, setInitialSearchState] = useSearchState();
    const mapRef = useRef<MapAPI>(null);
    const [searchState, dispatch] = useReducer(searchReducer, initialSearchState);

    useEffect(() => {
        setInitialSearchState(searchState);
    }, [searchState, setInitialSearchState]);

    const {ratingProfileId, searchQueryTags} = searchState;
    const placeTag = searchQueryTags.find(tag => tag.type == "place");
    const placeTagPosition = geoLocationFromPlaceSearchQueryTag(placeTag);

    const handleSelectedTagsChange = (queryTags: SearchQueryTagList) => {
        dispatch({ type: 'TAGS_CHANGED', searchQueryTags: queryTags });

        // move map to place tag if one has been added
        const prevPlaceTag = searchQueryTags.find(t => t.type == "place");
        const prevLocation = geoLocationFromPlaceSearchQueryTag(prevPlaceTag);
        const newPlaceTag = queryTags.find(t => t.type == "place");
        const newLocation = geoLocationFromPlaceSearchQueryTag(newPlaceTag);
        if (newLocation && !(newLocation.lat == prevLocation?.lat && newLocation.lng == prevLocation?.lng)) {
            const map = mapRef.current;
            if (map) {
                map.setView(newLocation);
            }
        }
    };

    const handleRatingProfileSelected = (newRatingProfileId: RatingProfileId) => {
        dispatch({ type: 'RATING_PROFILE_SELECTION_CHANGED', ratingProfileId: newRatingProfileId });
    };

    const handleMapMoved = (newMapInfo: MapInfo) => {
        dispatch({ type: 'MAP_INFO_CHANGED', mapInfo: newMapInfo });
    }

    return (
        <GeoPositionProvider>
            <div className={"h-full grid grid-cols-[1fr] l:grid-cols-[420px_1fr] grid-rows-[1fr_340px] l:grid-rows-[100%] print:grid-cols-[1fr] print:grid-rows-[1fr]"}>
                <div className={"relative col-span-full row-start-1 row-end-2 l:col-start-2 l:col-end-3 l:row-span-full print:hidden"}>
                    <RatingProfileSwitcher ratingProfileId={ratingProfileId} onRatingProfileSelected={handleRatingProfileSelected} />
                    <Map ref={mapRef} searchState={searchState} placeTagPosition={placeTagPosition} onMapSearch={handleMapMoved} />
                </div>
                <div className={`bg-page z-1000 col-span-full row-start-2 row-end-3 l:col-start-1 l:col-end-2 l:row-span-full l:shadow-[0_5px_10px_0px_rgba(0,0,0,0.05),0_15px_25px_0px_rgba(0,0,0,0.07)] print:row-span-full print:col-span-full print:pt-2 max-l:absolute max-l:bottom-0 max-l:w-full max-l:h-[340px] max-l:focus-within:h-full`}>
                    <SearchSidebar searchState={searchState} onSelectedQueryTagsChange={handleSelectedTagsChange} />
                </div>
            </div>
        </GeoPositionProvider>
    );
}
